import React, { useState, useEffect, useContext, useReducer, createContext } from "react";
import { useNavigate } from 'react-router-dom';

import { BASE_API_URL } from "./Common";

import axios from 'axios';
import { defaultThemeName } from '../app/Theme';
import { useThemeContext } from './ThemeService';
import { getAuthCookieSessionsEnabled } from '../components/auth/googleAuthConfig';

export const authRequestConfig = (config = {}) => {
    if (!getAuthCookieSessionsEnabled()) {
        return config;
    }
    return {
        ...config,
        withCredentials: true,
    };
};

export const buildGoogleLoginUrl = (baseApiUrl, { returnTo = "/login", forceReauth = false, termsAccepted = false } = {}) => {
    const safeReturnTo = (
        typeof returnTo === "string"
        && returnTo.startsWith("/")
        && !returnTo.startsWith("//")
        && !returnTo.includes("\\")
        && !returnTo.includes("#")
        && returnTo.length <= 512
    ) ? returnTo : "/login";
    const params = new URLSearchParams({ return_to: safeReturnTo });
    if (forceReauth === true) {
        params.set("force_reauth", "true");
    }
    if (termsAccepted === true) {
        params.set("terms_accepted", "true");
    }
    return `${baseApiUrl}/auth/google/login?${params.toString()}`;
};

export const authHeader = () => {
    if (getAuthCookieSessionsEnabled()) {
        return {};
    }
    const auth = JSON.parse(localStorage.getItem('auth'));

    if (auth && auth.access_token) {
        return { Authorization: 'Bearer ' + auth.access_token };
    } else {
        return {};
    }
}

// Local Storage Hook
function useLocalStorage(key, initialValue) {
    // State to store our value
    // Pass initial state function to useState so logic is only executed once
    const [storedValue, setStoredValue] = useState(() => {
        try {
            // Get from local storage by key
            const item = window.localStorage.getItem(key);
            // Parse stored json or if none return initialValue
            return item ? JSON.parse(item) : initialValue;
        } catch (error) {
            // If error also return initialValue
            console.log(error);
            return initialValue;
        }
    });
    // Return a wrapped version of useState's setter function that ...
    // ... persists the new value to localStorage.
    const setValue = (value) => {
        try {
            // Allow value to be a function so we have same API as useState
            const valueToStore =
                value instanceof Function ? value(storedValue) : value;
            // Save state
            setStoredValue(valueToStore);
            // Save to local storage
            window.localStorage.setItem(key, JSON.stringify(valueToStore));
        } catch (error) {
            // A more advanced implementation would handle the error case
            console.log(error);
        }
    };
    return [storedValue, setValue];
}

// Create an Auth Context
const AuthContext = createContext();

// Hook for child components to get the auth object
// and re-render when it changes.
export const useAuthContext = () => {
    return useContext(AuthContext);
};

// Setup Auth Reducer
let initialState = {
    isAuthenticated: false,
    username: null,
    full_name: null,
    access_token: null,
    token_type: null,
    auth_method: null,
    account_type: null,
    payment_plan: null,
    theme: defaultThemeName,
    profile_image_url: null
};
const hasOwn = (obj, key) => Object.prototype.hasOwnProperty.call(obj, key);
const reducer = (state, action) => {
    switch (action.type) {
        case "LOGIN":
            {
                const profileImage = hasOwn(action.payload, "profile_image_url")
                    ? action.payload.profile_image_url
                    : state.profile_image_url;
                const theme = hasOwn(action.payload, "theme")
                    ? (action.payload.theme || defaultThemeName)
                    : (state.theme || defaultThemeName);
                const storedAuth = {
                    ...(JSON.parse(localStorage.getItem('auth') || '{}')),
                    ...action.payload,
                    theme,
                    profile_image_url: profileImage,
                };
                localStorage.setItem("auth", JSON.stringify(storedAuth));

                return {
                    ...state,
                    isAuthenticated: true,
                    username: hasOwn(action.payload, "username") ? action.payload.username : state.username,
                    access_token: hasOwn(action.payload, "access_token") ? action.payload.access_token : state.access_token,
                    token_type: hasOwn(action.payload, "token_type") ? action.payload.token_type : state.token_type,
                    auth_method: hasOwn(action.payload, "auth_method") ? action.payload.auth_method : state.auth_method,
                    full_name: hasOwn(action.payload, "full_name") ? action.payload.full_name : state.full_name,
                    account_type: hasOwn(action.payload, "account_type") ? action.payload.account_type : state.account_type,
                    payment_plan: hasOwn(action.payload, "payment_plan") ? action.payload.payment_plan : state.payment_plan,
                    theme,
                    profile_image_url: profileImage ?? null,
                };
            }
        case "LOGOUT":
            localStorage.removeItem('auth');
            return {
                ...state,
                isAuthenticated: false,
                username: null,
                auth_method: null,
                account_type: null,
                payment_plan: null,
                profile_image_url: null,
                theme: defaultThemeName
            };
        case "PROFILE":
            {
                const profileImage = hasOwn(action.payload, "profile_image_url")
                    ? action.payload.profile_image_url
                    : state.profile_image_url;
                const theme = hasOwn(action.payload, "theme")
                    ? (action.payload.theme || defaultThemeName)
                    : state.theme;
                const fullName = hasOwn(action.payload, "full_name")
                    ? action.payload.full_name
                    : state.full_name;
                const accountType = hasOwn(action.payload, "account_type")
                    ? action.payload.account_type
                    : state.account_type;
                const paymentPlan = hasOwn(action.payload, "payment_plan")
                    ? action.payload.payment_plan
                    : state.payment_plan;

                const currentAuth = JSON.parse(localStorage.getItem('auth') || '{}');
                const updatedAuth = {
                    ...currentAuth,
                    username: currentAuth.username ?? state.username,
                    access_token: currentAuth.access_token ?? state.access_token,
                    token_type: currentAuth.token_type ?? state.token_type,
                    full_name: fullName,
                    account_type: accountType,
                    payment_plan: paymentPlan,
                    theme,
                    profile_image_url: profileImage,
                };
                localStorage.setItem('auth', JSON.stringify(updatedAuth));

                return {
                    ...state,
                    full_name: fullName,
                    account_type: accountType,
                    payment_plan: paymentPlan,
                    theme,
                    profile_image_url: profileImage,
                };
            }
        default:
            return state;
    }
};

// Auth Context Provider component that wraps your app and makes auth object
// available to any child component that calls useAuth().
export function AuthContextProvider({ children }) {
    console.log("================================== AuthContextProvider ======================================");

    // Check if user was already logged in in local storage
    const auth = JSON.parse(localStorage.getItem('auth') || null);
    if (auth) {
        initialState["isAuthenticated"] = true;
        initialState["username"] = auth.username;
        initialState["access_token"] = auth.access_token;
        if (auth.token_type) {
            initialState["token_type"] = auth.token_type;
        }
        if (auth.auth_method) {
            initialState["auth_method"] = auth.auth_method;
        }
        if (auth.full_name) {
            initialState["full_name"] = auth.full_name;
        }
        if (auth.account_type) {
            initialState["account_type"] = auth.account_type;
        }
        if (auth.payment_plan) {
            initialState["payment_plan"] = auth.payment_plan;
        }
        if (auth.theme) {
            initialState["theme"] = auth.theme;
        }
        initialState["profile_image_url"] = hasOwn(auth, "profile_image_url") ? auth.profile_image_url : initialState["profile_image_url"];
    }

    // Auth Reducer
    const [state, dispatch] = useReducer(reducer, initialState);
    const { setThemeName } = useThemeContext();

    // Setup Provider
    useEffect(() => {
        console.log(state);
        if (state.isAuthenticated) {
            // Validate Auth
            AuthService.Validate()
                .then(function (response) {
                    console.log(response.data);
                    dispatch({
                        type: "LOGIN",
                        payload: response.data
                    })

                    // Get User Profile
                    return AuthService.GetProfile()
                })
                .then(function (response) {
                    let profile = response.data;
                    dispatch({
                        type: "PROFILE",
                        payload: profile
                    })
                    if (profile && profile.theme) {
                        setThemeName(profile.theme);
                    }
                })
                .catch(function (error) {
                    console.log(error.response);
                    dispatch({
                        type: "LOGOUT"
                    })
                })
        }
    }, []);

    useEffect(() => {
        if (state.theme) {
            setThemeName(state.theme);
        }
    }, [state.theme, setThemeName]);

    return <AuthContext.Provider value={{ state, dispatch }}>{children}</AuthContext.Provider>;
}

// Provider hook that creates auth object and handles state
export const useProvideAuth = () => {
    const [user, setUser] = useState(null);
    const [auth, setAuth] = useLocalStorage("auth", null);

    let navigate = useNavigate();

    const login = (username, password) => {
        return {};
    };
    const signup = (username, password) => {
        return {};
    };
    const logout = () => {
        return {};
    };
    const verify = () => {
        if (!user) {
            navigate("/login");
        }
    }
    const valid = () => {
        if (!user) {
            return false;
        } else {
            return true;
        }
    }

    // Subscribe to user on mount
    // Because this sets state in the callback it will cause any ...
    // ... component that utilizes this hook to re-render with the ...
    // ... latest auth object.
    useEffect(() => {
        const auth = JSON.parse(localStorage.getItem('auth'));
        console.log(auth);
        if (auth) {
            setUser(auth.username);
        }
    }, []);

    // Return the user object and auth methods
    return {
        user,
        login,
        signup,
        logout,
        verify,
        valid,
    };
}

const AuthService = {
    Init: function () {
        // Any application initialization logic comes here
    },
    Signup: async function (username, email, password,) {
        
        if (username=='') {username=' ';}
        if (password=='') {password=' ';}
        if (email=='') {email=' ';}
        var formData = new FormData();
        formData.append("grant_type", "password");
        formData.append("username", username);
        formData.append("password", password);
        formData.append("email", email);

        return await axios.post(BASE_API_URL + "/auth/signup",
            formData,
            authRequestConfig({
                headers: { "Content-Type": "application/x-www-form-urlencoded" }
            })
        );
    },
    Login: async function (username, password,) {

        var formData = new FormData();
        formData.append("grant_type", "password");
        formData.append("username", username);
        formData.append("password", password);

        return await axios.post(BASE_API_URL + "/auth/login",
            formData,
            authRequestConfig({
                headers: { "Content-Type": "application/x-www-form-urlencoded" }
            })
        );
    },
    GoogleLogin: function ({ returnTo = "/login", forceReauth = false, termsAccepted = false } = {}) {
        window.location.assign(buildGoogleLoginUrl(BASE_API_URL, { returnTo, forceReauth, termsAccepted }));
    },
    ConsumeGoogleCallback: function () {
        const hash = window.location.hash || "";
        if (!hash.startsWith("#")) {
            const query = new URLSearchParams(window.location.search);
            if (getAuthCookieSessionsEnabled() && query.get("auth_transport") === "cookie") {
                window.history.replaceState({}, document.title, window.location.pathname + window.location.search);
                return { auth_method: "google", auth_transport: "cookie" };
            }
            return null;
        }

        const params = new URLSearchParams(hash.slice(1));
        const accessToken = params.get("access_token");
        const tokenType = params.get("token_type") || "bearer";
        window.history.replaceState({}, document.title, window.location.pathname + window.location.search);

        if (!accessToken || accessToken.length > 8192 || tokenType.toLowerCase() !== "bearer") {
            return null;
        }

        return {
            access_token: accessToken,
            token_type: "bearer",
            auth_method: "google",
        };
    },
    Reauthenticate: async function (password) {
        let currentAuth;
        try {
            currentAuth = JSON.parse(localStorage.getItem('auth') || '{}');
        } catch (error) {
            throw new Error('The current authentication session is invalid.');
        }
        if (!currentAuth.username || typeof password !== 'string' || !password) {
            throw new Error('Recent password authentication is required.');
        }
        const response = await this.Login(currentAuth.username, password);
        if (!response?.data?.access_token && !getAuthCookieSessionsEnabled()) {
            throw new Error('Recent password authentication failed.');
        }
        // The password is sent only to the login endpoint and is never persisted.
        const updatedAuth = { ...currentAuth, ...response.data };
        if (getAuthCookieSessionsEnabled()) {
            delete updatedAuth.access_token;
        }
        localStorage.setItem('auth', JSON.stringify(updatedAuth));
        return response;
    },
    Logout: async function () {
        return await axios.post(BASE_API_URL + "/auth/logout", {}, authRequestConfig());
    },
    Validate: async function () {
        return await axios.post(BASE_API_URL + "/auth/validate", {}, authRequestConfig({ headers: authHeader() }));
    },
    GetProfile: async function () {
        return await axios.get(BASE_API_URL + "/profile", authRequestConfig({ headers: authHeader() }));
    },
    SaveProfile: async function (profile) {
        return await axios.put(BASE_API_URL + "/profile", profile, authRequestConfig({ headers: authHeader() }));
    },
    GetAccount: async function () {
        return await axios.get(BASE_API_URL + "/profile", authRequestConfig({ headers: authHeader() }));
    },
    ChangePassword: async function (previous_password, new_password) {
        return await axios.put(BASE_API_URL + "/auth/password", { previous_password, new_password }, authRequestConfig({ headers: authHeader() }));
    },
    UpdateThemePreference: async function (theme) {
        return await axios.put(BASE_API_URL + "/profile/theme", { theme }, authRequestConfig({ headers: authHeader() }));
    },
    UpdateProfileImage: async function (profileImageUrl) {
        return await axios.put(BASE_API_URL + "/profile/photo", { profile_image_url: profileImageUrl }, authRequestConfig({ headers: authHeader() }));
    },
    GetAPIKeys: async function () {
        return await axios.get(BASE_API_URL + "/api_keys", authRequestConfig({ headers: authHeader() }));
    },
    CreateAPIKey: async function () {
        return await axios.post(BASE_API_URL + "/api_keys", {}, authRequestConfig({ headers: authHeader() }));
    },
    DeleteAPIKey: async function (id) {
        return await axios.delete(BASE_API_URL + "/api_keys/" + id, authRequestConfig({ headers: authHeader() }));
    },
    ResetPassword: async function (email) {
        return await axios.post(BASE_API_URL + "/auth/resetpassword", { "email": email }, authRequestConfig({ headers: authHeader() }));
    },
    ConfirmResetPassword: async function (token, new_password) {
        return await axios.post(
            BASE_API_URL + "/auth/resetpassword/confirm",
            { token, new_password },
            authRequestConfig({ headers: authHeader() })
        );
    },
}
export default AuthService;
